import React, { useEffect, useState } from 'react';
import { Plus, Download, Edit, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useQRStore } from '../store/useQRStore';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthProvider';
import { listMyStaticQR, StaticQRRow } from '../services/staticQRService';
import { listMyDynamicQR, DynamicQRRow } from '../services/sbDynamicQR';
import { DynamicQREditModal } from '../components/qr/DynamicQREditModal';

export const DashboardPage: React.FC = () => {
  const { qrCodes, dynamicQRCodes } = useQRStore();
  const { session } = useAuth();
  const [staticQRCodes, setStaticQRCodes] = useState<StaticQRRow[]>([]);
  const [dynamicQRCodesDB, setDynamicQRCodesDB] = useState<DynamicQRRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingQR, setEditingQR] = useState<DynamicQRRow | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (session) {
      fetchQRCodes();
    }
  }, [session]);

  const fetchQRCodes = async () => {
    setIsLoading(true);
    try {
      const [staticData, dynamicData] = await Promise.all([
        listMyStaticQR(50, 0),
        listMyDynamicQR(50, 0)
      ]);
      setStaticQRCodes(staticData);
      setDynamicQRCodesDB(dynamicData);
    } catch (error) {
      console.error('Failed to fetch QR codes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditQR = (qrCode: any) => {
    // Find the actual dynamic QR code from our database
    const dynamicQR = dynamicQRCodesDB.find(dqr => 
      dqr.short_code === qrCode.id || 
      dqr.title === qrCode.title ||
      dqr.original_url === qrCode.content
    );
    
    if (dynamicQR) {
      setEditingQR(dynamicQR);
      setShowEditModal(true);
    }
  };

  const handleEditSave = () => {
    // Refresh the QR codes after edit
    fetchQRCodes();
    setShowEditModal(false);
    setEditingQR(null);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setEditingQR(null);
  };

  // Combine all QR codes for display
  const allQRCodes = [
    ...qrCodes, 
    ...dynamicQRCodes,
    ...staticQRCodes.map(qr => ({
      id: qr.id.toString(),
      content: qr.content,
      type: qr.type as any,
      title: qr.title,
      createdAt: new Date(qr.created_at),
      isDynamic: false,
    })),
    ...dynamicQRCodesDB.map(qr => ({
      id: qr.id.toString(),
      content: qr.original_url,
      type: 'url' as any,
      title: qr.title,
      createdAt: new Date(qr.created_at),
      isDynamic: true,
      scanCount: qr.analytics?.totalScans || 0,
    }))
  ];

  if (!session) {
    return (
      <div className="max-w-7xl mx-auto text-center py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Your QR Code Dashboard
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          Sign in to view and manage your saved QR codes
        </p>
        <Link to="/account">
          <Button>
            Sign In to View Dashboard
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Your QR Codes
          </h1>
          <p className="text-lg text-gray-600">
            Manage and track all your generated QR codes
          </p>
        </div>
        <Link to="/generate">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Create New QR Code
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-blue-600">{allQRCodes.length}</div>
            <p className="text-sm text-muted-foreground">Total QR Codes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-green-600">
              {staticQRCodes.length + qrCodes.length}
            </div>
            <p className="text-sm text-muted-foreground">Static QR Codes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-purple-600">
              {dynamicQRCodesDB.length + dynamicQRCodes.length}
            </div>
            <p className="text-sm text-muted-foreground">Dynamic QR Codes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="text-2xl font-bold text-orange-600">
              {dynamicQRCodesDB.reduce((sum, qr) => sum + (qr.analytics?.totalScans || 0), 0) +
               dynamicQRCodes.reduce((sum, qr) => sum + qr.scanCount, 0)}
            </div>
            <p className="text-sm text-muted-foreground">Total Scans</p>
          </CardContent>
        </Card>
      </div>

      {/* QR Codes List */}
      {isLoading ? (
        <Card>
          <CardContent className="text-center py-16">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-muted-foreground">Loading your QR codes...</p>
          </CardContent>
        </Card>
      ) : allQRCodes.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Recent QR Codes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {allQRCodes.slice(0, 10).map((qrCode) => (
                <div
                  key={qrCode.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                      📱
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {qrCode.title || `${qrCode.type.toUpperCase()} QR Code`}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {qrCode.content.length > 50 
                          ? `${qrCode.content.substring(0, 50)}...` 
                          : qrCode.content
                        }
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Created: {qrCode.createdAt?.toLocaleDateString()}
                        {qrCode.isDynamic && ` • Dynamic • ${(qrCode as any).scanCount || 0} scans`}
                        {!qrCode.isDynamic && ' • Static'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                    </Button>
                    {qrCode.isDynamic && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleEditQR(qrCode)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="text-center py-16">
            <div className="text-6xl mb-4">📱</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No QR Codes Yet
            </h3>
            <p className="text-gray-600 mb-6">
              Create your first QR code to get started!
            </p>
            <Link to="/generate">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First QR Code
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      {editingQR && (
        <DynamicQREditModal
          qrCode={editingQR}
          isOpen={showEditModal}
          onClose={handleEditClose}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};
